"use client";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";
import { useStampCard } from "./hooks";

export interface StampCardDemoData {
  ariaLabel: string;
  title: string;
  total: number;
  dotsLabel: string;
  // "{n} of {total} visits"
  countTemplate: string;
  addLabel: string;
  resetLabel: string;
  unlockedLabel: string;
  reward: string;
  code: string;
  redeemLabel: string;
}

// "Try a stamp card" - the preview's `stamp_card` visual. Each click adds a
// stamp; at the goal a green reward card appears with a code the staff mark
// redeemed.
const StampCardDemo = ({
  ariaLabel,
  title,
  total,
  dotsLabel,
  countTemplate,
  addLabel,
  resetLabel,
  unlockedLabel,
  reward,
  code,
  redeemLabel,
}: StampCardDemoData) => {
  const { visits, isUnlocked, addVisit, reset, redeem, addRef } =
    useStampCard(total);
  const count = countTemplate
    .replace("{n}", String(visits))
    .replace("{total}", String(total));

  return (
    <figure role="group" aria-label={ariaLabel} className="m-0">
      <div className="mx-auto grid max-w-[820px] items-center gap-5 md:grid-cols-2">
        <div className="grid gap-3.5 rounded-[26px] bg-white p-[26px] shadow-card">
          <b className="text-[1.2rem] leading-snug font-bold">{title}</b>
          <div role="img" aria-label={dotsLabel} className="flex flex-wrap gap-3">
            {Array.from({ length: total }, (_, index) => {
              const isFilled = index < visits;
              return (
                <i
                  key={index}
                  className={cn(
                    "grid size-[46px] place-items-center rounded-full border-[3px] transition-colors duration-300 motion-reduce:transition-none",
                    isFilled
                      ? "border-solid border-primary bg-primary"
                      : "border-dashed border-line",
                  )}
                >
                  {isFilled && (
                    <Check className="size-5 text-white" strokeWidth={3} aria-hidden="true" />
                  )}
                </i>
              );
            })}
          </div>
          <p aria-live="polite" className="text-[15px] font-bold text-ink-muted">
            {count}
          </p>
          <div className="flex flex-wrap gap-2.5">
            <Button
              ref={addRef}
              type="button"
              variant="brand"
              size="pillSm"
              onClick={addVisit}
              disabled={isUnlocked}
            >
              {addLabel}
            </Button>
            <Button type="button" variant="brandOutline" size="pillSm" onClick={reset}>
              {resetLabel}
            </Button>
          </div>
        </div>

        <div aria-live="polite">
          {isUnlocked && (
            <div className="grid gap-3.5 rounded-[26px] bg-tone-mint-ink p-[26px] text-white shadow-card">
              <span className="text-[13px] font-bold opacity-90">{unlockedLabel}</span>
              <b className="text-[1.9rem] leading-tight font-bold">{reward}</b>
              <span className="justify-self-start rounded-full bg-white px-4 py-1.5 text-[15px] font-extrabold text-tone-mint-ink">
                {code}
              </span>
              <Button
                type="button"
                variant="white"
                size="pillSm"
                onClick={redeem}
                className="justify-self-start"
              >
                {redeemLabel}
              </Button>
            </div>
          )}
        </div>
      </div>
    </figure>
  );
};

export default StampCardDemo;
